import axios, { type InternalAxiosRequestConfig } from 'axios';
import { authStore } from '@/lib/auth/authStore';
import { lockStore } from '@/lib/auth/lockStore';
import { decodeBinaryErrorBody, normalizeError } from './errors';

/**
 * The one axios instance. Import `api` in endpoint modules (src/lib/api/*.ts); never use axios directly.
 *
 * - baseURL: VITE_API_URL (default '/api/v1', proxied to the backend by Vite in dev).
 * - request: attaches `Authorization: Bearer <token>`.
 * - response errors are ALWAYS rejected as `ApiError` ({ message, code, status, errors }).
 * - global side effects:
 *     401 (not on /auth/login)               -> sign out locally; RequireAuth redirects to /login
 *     423 SYSTEM_LOCKED                      -> full-screen "System locked" page
 *     403 PASSWORD_CHANGE_REQUIRED           -> forced change-password page
 */
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '/api/v1',
  headers: { Accept: 'application/json' },
  timeout: 30_000,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { token } = authStore.getState();
  if (token) config.headers.set('Authorization', `Bearer ${token}`);
  return config;
});

function isLoginRequest(url: string | undefined): boolean {
  return !!url && /\/auth\/login\/?$/.test(url);
}

api.interceptors.response.use(
  (response) => {
    // Any successful authenticated response proves the lock is gone (e.g. super_admin unlocked it).
    return response;
  },
  async (error: unknown) => {
    await decodeBinaryErrorBody(error);
    const apiError = normalizeError(error);
    const url = (error as { config?: { url?: string } })?.config?.url;

    if (apiError.status === 401 && !isLoginRequest(url) && authStore.getState().token) {
      authStore.clear('expired');
    } else if (apiError.status === 423 && apiError.code === 'SYSTEM_LOCKED') {
      lockStore.lock(apiError.message);
    } else if (apiError.status === 403 && apiError.code === 'PASSWORD_CHANGE_REQUIRED') {
      authStore.markPasswordChangeRequired();
    }
    return Promise.reject(apiError);
  },
);
